import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SingleSelect from "../../../../component/singleSelect/SingleSelect";
import Pagination from "../../../../component/pagination/Pagination";
import OptionSixJobViewPage from "./OptionSixJobViewPage";
import OptionSixNoJob from "./OptionSixNoJob";
import OptionSixJobFilter from "./OptionSixJobFilter";
import locationIcon from "../../../../assets/images/locationicon.svg";
import DataIcon from "../../../../assets/images/date-icon.svg";
import Image from "../../../../component/image/Image";
import "./option-six.scss";

const OptionSixJobListing = (props: any) => {
    const {
        location,
        checkListControl,
        jobsList,
        setTableControl,
        tableControl,
        sortList,
        pageSizeChange,
        displayPage,
        moveToJobViewPage,
        publishingDate,
        pageChange,
        filterListData,
        searchControlSubmit,
        navigateToJob,
        getListOfJob,
    } = props;

    const [viewMode, setViewMode] = useState<"grid" | "card">("grid");
    const clientConfig = useSelector((state: any) => state.careers.cliendConfig);
    const langLabels = clientConfig?.data?.data?.clientSettings?.languageSettings;
    const language = clientConfig?.data?.data?.clientSettings?.language;
    const showJobApplyButton = clientConfig?.data?.data?.clientSettings?.showJobApplyButtonInListing;
    const primaryColor = clientConfig?.data?.data?.clientSettings?.primaryColor || "#d0b06e";

    const opt6ToolbarStyles = useMemo(() => ({
        control: (styles: any, { isFocused }: any) => ({
            ...styles,
            minHeight: "38px",
            minWidth: "150px",
            background: "#101722",
            border: `1px solid ${isFocused ? primaryColor : "#273446"}`,
            borderRadius: "8px",
            boxShadow: isFocused ? `0 0 0 1px ${primaryColor}40` : "none",
            "&:hover": { borderColor: primaryColor },
            cursor: "pointer",
        }),
        menu: (styles: any) => ({
            ...styles,
            background: "#101722",
            border: "1px solid #273446",
            borderRadius: "8px",
            marginTop: "6px",
            zIndex: 20,
        }),
        menuList: (styles: any) => ({
            ...styles,
            padding: "4px",
        }),
        option: (styles: any, { isFocused, isSelected }: any) => ({
            ...styles,
            background: isSelected ? `${primaryColor}28` : isFocused ? "#1c2535" : "transparent",
            color: isSelected ? primaryColor : "#d6dce7",
            padding: "8px 12px",
            borderRadius: "6px",
            fontSize: "14px",
            cursor: "pointer",
            ":active": { background: `${primaryColor}28`, color: primaryColor },
        }),
        placeholder: (styles: any) => ({ ...styles, color: "#7f8da3", margin: 0 }),
        valueContainer: (styles: any) => ({ ...styles, padding: "4px 12px" }),
        singleValue: (styles: any) => ({ ...styles, color: "#d6dce7" }),
        input: (styles: any) => ({ ...styles, color: "#d6dce7", margin: 0, padding: 0 }),
        indicatorSeparator: () => ({ display: "none" }),
        dropdownIndicator: (styles: any) => ({ ...styles, color: "#8b96a9", paddingRight: "10px" }),
    }), [primaryColor]);
    const jobListingLangLabels = langLabels?.jobListing?.[0];
    const { t } = useTranslation();
    const navigate = useNavigate();

    const selectedPageSize = {
        value: tableControl?.pageSize?.value,
        label: tableControl?.pageSize?.value + " " + t(" per page")
    };

    const selectedSortList = {
        value: tableControl?.sort?.value,
        label: t(tableControl?.sort?.label)
    };

    const langSortList = sortList.map((item: any) => ({
        value: item.value,
        label: t(item.label)
    }));

    const langPageSize = displayPage.map((item: { value: string }) => ({
        value: item.value,
        label: item.value + " " + t(" per page")
    }));

    const marqueeItems = useMemo(() => {
        const evp = [
            "MAKE AN IMPACT", "GROW WITH US", "INCLUSIVE CULTURE", "FLEXIBLE WORKING",
            "CAREER DEVELOPMENT", "COLLABORATIVE TEAM", "COMPETITIVE BENEFITS", "MEANINGFUL WORK",
            "INNOVATION DRIVEN", "WORK LIFE BALANCE", "PURPOSE LED", "DIVERSE & INCLUSIVE"
        ];
        const fromCategory = (filterListData?.jobCategories || []).map((c: any) => c?.label).filter(Boolean);
        const fromTypes = (filterListData?.jobTypes || []).map((c: any) => c?.label).filter(Boolean);
        const fromData = Array.from(new Set([...fromCategory, ...fromTypes])).slice(0, 8);
        return [...evp, ...fromData];
    }, [filterListData]);

    const navigateToJobAlerts = () => {
        navigate("/job-alerts");
    };

    const navigateToApplyJob = (jobId: string) => {
        navigate(`/view/${jobId}/apply-job/from/direct`);
    };

    if (location.pathname !== "/") {
        return (
            <OptionSixJobViewPage
                jobDataView={
                    <OptionSixJobFilter
                        filter={filterListData}
                        tableControl={tableControl}
                        setTableControl={setTableControl}
                        searchControlSubmit={searchControlSubmit}
                    />
                }
                navigateToJob={navigateToJob}
                getListOfJob={getListOfJob}
            />
        );
    }

    return (
        <div className="option6-jobs">
            <div className="option6-marquee-wrap">
                <div className="option6-marquee-track option6-marquee-track-a">
                    {marqueeItems.map((item, idx) => (
                        <span key={`${item}-${idx}`}>{item}</span>
                    ))}
                </div>
                <div className="option6-marquee-track option6-marquee-track-b" aria-hidden="true">
                    {marqueeItems.map((item, idx) => (
                        <span key={`${item}-clone-${idx}`}>{item}</span>
                    ))}
                </div>
                <div className="option6-marquee-track option6-marquee-track-b" aria-hidden="true">
                    {marqueeItems.map((item, idx) => (
                        <span key={`${item}-clone-${idx}`}>{item}</span>
                    ))}
                </div>
            </div>

            <div className="option6-shell">
                <div className="option6-heading">
                    <p className="option6-kicker">{t("Browse Positions")}</p>
                    <h2>{t("Find your")} <em>{t("perfect role")}</em></h2>
                </div>

                <OptionSixJobFilter
                    filter={filterListData}
                    tableControl={tableControl}
                    setTableControl={setTableControl}
                    searchControlSubmit={searchControlSubmit}
                />

                <div className="option6-toolbar">
                    <div className="option6-count">
                        <strong>{jobsList?.data?.data?.jobs?.totalRecord || 0}</strong> {checkListControl ? t("search results") : t("open positions")}
                    </div>
                    <div className="option6-toolbar-right">
                        <SingleSelect
                            className="sort-control option6-sort-select"
                            placeholder=""
                            onChange={(e: any) => {
                                setTableControl((prs: any) => ({
                                    ...prs,
                                    sort: e
                                }));
                            }}
                            value={selectedSortList}
                            options={langSortList}
                            isSearchable={false}
                            customStyles={opt6ToolbarStyles}
                        />
                        <SingleSelect
                            className="sort-control option6-page-size"
                            placeholder=""
                            onChange={(e: any) => pageSizeChange(e)}
                            value={selectedPageSize}
                            options={langPageSize}
                            isSearchable={false}
                            customStyles={opt6ToolbarStyles}
                        />
                        <div className="option6-view-toggle">
                            <button className={viewMode === "grid" ? "active" : ""} onClick={() => setViewMode("grid")}><i className="pi pi-th-large"></i></button>
                            <button className={viewMode === "card" ? "active" : ""} onClick={() => setViewMode("card")}><i className="pi pi-bars"></i></button>
                        </div>
                    </div>
                </div>

                {tableControl.data.length > 0 ? (
                    <>
                        <div className={`option6-list ${viewMode === "card" ? "is-card" : "is-grid"}`}>
                            {tableControl.data.map((job: any) => (
                                <article key={job.id} className="option6-card">
                                    <div className="option6-card-main">
                                        <div className="option6-card-top">
                                            <span className="option6-tag">{(job?.jobType?.typeName || "Role").toUpperCase()}</span>
                                            <time>{job.postedAt !== "" ? publishingDate(job.postedAt, language) : ""}</time>
                                        </div>

                                        <h3 role="button" onClick={() => moveToJobViewPage(job)}>{job.title}</h3>

                                        <div className="option6-meta">
                                            {job.location?.location !== "" ? (
                                                <span><Image src={locationIcon} /> {job.location?.location}</span>
                                            ) : (
                                                ""
                                            )}
                                            {job.postedAt !== "" ? (
                                                <span><Image src={DataIcon} /> {publishingDate(job.postedAt, language)}</span>
                                            ) : (
                                                ""
                                            )}
                                        </div>

                                        <div className="option6-desc" dangerouslySetInnerHTML={{ __html: job.description }} />
                                    </div>

                                    <div className="option6-card-footer">
                                        <div className="option6-actions">
                                            <button className="option6-btn option6-btn-muted" onClick={() => moveToJobViewPage(job)}>{t("Learn More")}</button>
                                            {showJobApplyButton === 1 ? (
                                                <button className="option6-btn option6-btn-brand" onClick={() => navigateToApplyJob(job.id)}>{t("Apply Now")}</button>
                                            ) : (
                                                ""
                                            )}
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>

                        <div className="option6-pagination">
                            <Pagination
                                className="pagination-bar"
                                currentPage={tableControl.currentPage}
                                totalCount={tableControl.totalCount}
                                pageSize={tableControl.pageSize?.value}
                                onPageChange={(page: any) => pageChange(page)}
                            />
                        </div>
                    </>
                ) : null}

                <OptionSixNoJob navigateToJob={navigateToJob} navigateToJobAlerts={navigateToJobAlerts} />
            </div>
        </div>
    );
};

export default OptionSixJobListing;
